import React from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import {ICONS} from '../../lib/curo-icons.js';
import {GUIDES, guideL, guideStep} from '../../lib/curo-guides.js';

/**
 * 따라하기 가이드 패널 — 큐로 신기능을 단계별로 안내(이미지 자산 0장).
 * 2026-07-24. 상단바 "따라하기" 버튼 → 커스텀 이벤트 curoai-open-guide 로 열림.
 *
 * ⭐ 스크래치 표준 덱을 안 쓴 이유: 표준 덱은 스텝마다 GIF 자산이 필수(디자인 대기).
 *    대신 **텍스트 단계 + 실제 동작 버튼**(탭 이동·확장 갤러리·리포트 열기)으로 구성 —
 *    말로만 설명하지 않고 눌러서 진짜로 되게 한다.
 *
 * 배치: 우하단 작은 패널(모달 아님) — 가이드를 보면서 **동시에 에디터를 조작**해야 하므로
 *      화면을 가리는 오버레이는 쓰지 않는다.
 */

class GuidePanel extends React.Component {
    constructor (props) {
        super(props);
        this.state = {open: false, guide: null, step: 0};
        this._open = this._open.bind(this);
        this.close = this.close.bind(this);
    }
    componentDidMount () { window.addEventListener('curoai-open-guide', this._open); }
    componentWillUnmount () { window.removeEventListener('curoai-open-guide', this._open); }
    // 가이드가 열려 있는 동안엔 쉬는시간 제안이 같은 자리(우하단)에 겹치지 않게 알린다
    _flag (on) { try { window.__curoGuideOpen = !!on; } catch (e) { /* noop */ } }
    _open () { this._flag(true); this.setState({open: true, guide: null, step: 0}); }
    close () { this._flag(false); this.setState({open: false}); }
    pick (g) { this.setState({guide: g, step: 0}); }
    back () {
        if (this.state.step > 0) this.setState({step: this.state.step - 1});
        else this.setState({guide: null, step: 0}); // 목록으로
    }
    next () {
        const g = this.state.guide;
        if (!g) return;
        if (this.state.step < g.steps.length - 1) this.setState({step: this.state.step + 1});
        else this.close();
    }
    // 단계의 action 실행 — 실제로 그 일이 일어나게
    run (action) {
        if (!action) return;
        const p = this.props;
        try {
            if (action === 'tab:ai' && p.onGoAi) p.onGoAi();
            else if (action === 'tab:python' && p.onGoPython) p.onGoPython();
            else if (action === 'tab:code' && p.onGoCode) p.onGoCode();
            else if (action === 'ext' && p.onGoExtension) p.onGoExtension();
            else if (action === 'report') window.dispatchEvent(new CustomEvent('curoai-open-report'));
        } catch (e) { /* noop — 동작 실패해도 가이드는 계속 */ }
    }
    _loc () { return (this.props.intl && this.props.intl.locale) || 'en'; }
    render () {
        if (!this.state.open) return null;
        const loc = this._loc();
        const L = guideL(loc);
        const g = this.state.guide;

        return (
            <div style={S.panel}>
                <div style={S.head}>
                    <span style={S.title}>{g ? L.g[g.key] : L.title}</span>
                    <button style={S.x} onClick={this.close} aria-label="Close">
                        <span
                            style={{display: 'inline-flex', fontSize: 16}}
                            dangerouslySetInnerHTML={{__html: '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>'}}
                        />
                    </button>
                </div>

                {!g ? (
                    <div>
                        <div style={S.sub}>{L.sub}</div>
                        <div style={S.list}>
                            {GUIDES.map(item => (
                                <button key={item.key} style={S.item} onClick={() => this.pick(item)}>
                                    <span
                                        style={Object.assign({}, S.dot, {background: item.color})}
                                        dangerouslySetInnerHTML={{__html: ICONS[item.icon] || ''}}
                                    />
                                    <span style={S.itemName}>{L.g[item.key]}</span>
                                    <span style={S.chev}>{'›'}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div>
                        <div style={S.stepNo}>{`${L.step} ${this.state.step + 1} / ${g.steps.length}`}</div>
                        <div style={S.stepText}>{guideStep(loc, g.steps[this.state.step].t)}</div>
                        {g.steps[this.state.step].action ? (
                            <button
                                style={Object.assign({}, S.doBtn, {background: g.color})}
                                onClick={() => this.run(g.steps[this.state.step].action)}
                            >
                                {L.g[g.key]}
                            </button>
                        ) : null}
                        <div style={S.nav}>
                            <button style={S.ghost} onClick={() => this.back()}>{L.prev}</button>
                            <button style={S.primary} onClick={() => this.next()}>
                                {this.state.step < g.steps.length - 1 ? L.next : L.done}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

const S = {
    // 오버레이 없음 — 가이드를 보면서 에디터를 만져야 하므로
    panel: {position: 'fixed', right: 16, bottom: 16, zIndex: 9200, width: 286,
        background: '#fff', borderRadius: 14, border: '1px solid #e0e3ec',
        boxShadow: '0 10px 32px rgba(0,0,0,0.22)', padding: '13px 15px 15px',
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif'},
    head: {display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8},
    title: {fontSize: 14, fontWeight: 'bold', color: '#1f2540'},
    x: {border: 'none', background: 'transparent', color: '#9aa1b4', cursor: 'pointer', padding: 2, lineHeight: 1},
    sub: {fontSize: 11, color: '#8a90a3', marginTop: 3},
    list: {display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10},
    item: {display: 'flex', alignItems: 'center', gap: 9, padding: '9px 10px', border: '1px solid #e6e8f0',
        borderRadius: 10, background: '#fff', cursor: 'pointer', textAlign: 'left', width: '100%'},
    dot: {width: 26, height: 26, borderRadius: '50%', color: '#fff', fontSize: 14, flex: 'none',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center'},
    itemName: {fontSize: 13, color: '#1f2540', flex: 1},
    chev: {color: '#c3c8d6', fontSize: 16},
    stepNo: {fontSize: 11, color: '#9aa1b4', marginTop: 9},
    stepText: {fontSize: 13, color: '#2b3350', lineHeight: 1.6, marginTop: 4},
    doBtn: {border: 'none', borderRadius: 16, color: '#fff', fontSize: 12, padding: '7px 14px',
        cursor: 'pointer', marginTop: 10},
    nav: {display: 'flex', justifyContent: 'space-between', marginTop: 14},
    ghost: {border: 'none', background: '#eef0f5', color: '#4a5169', fontSize: 12, borderRadius: 14,
        padding: '6px 13px', cursor: 'pointer'},
    primary: {border: 'none', background: '#14c8a6', color: '#fff', fontSize: 12, borderRadius: 14,
        padding: '6px 15px', cursor: 'pointer'}
};

GuidePanel.propTypes = {
    intl: PropTypes.object,
    onGoAi: PropTypes.func,
    onGoCode: PropTypes.func,
    onGoExtension: PropTypes.func,
    onGoPython: PropTypes.func
};

export default injectIntl(GuidePanel);
